import { GuideLiteEntry, GuideBubble, CheckinCard } from "../Guide/GuideLitePage";
import { ReclaimBar } from "../../components/ecosystem/ReclaimNav";
/**
 * Soulumo Foundations · Home  ·  Recipient experience
 *
 * Feb 22 2026 · Michael's final refinement pass.
 *
 *   • Deep navy + warm champagne-gold. Living Soulumo orb at top.
 *   • Hero copy verbatim per brief.
 *   • Primary CTA: TAKE A MOMENT · 60 seconds · a soft start.
 *   • Chooser: A moment for me → A Moment for Me · 60s
 *             Help me settle → Peace · 3m
 *             Help me rest   → Rest · 3m
 *     Pills hide when their target isn't ready (recipient never sees
 *     unfinished items).
 *   • EXPLORE THE FOUNDATIONS is a single-column, expandable list of
 *     five sections. Only READY installations render. Every card is
 *     name + one-line description + duration + play — no dev language,
 *     no line counts, no "recording · read script" states.
 *   • Session-expired banner shows only on real 401/403.
 *   • Footer link opens a warm, source-aware "How this gift came to
 *     you" sheet.
 *   • No autoplay. No streak pressure. No production terminology.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SoulumoWord from "../../components/SoulumoWord";
import FoundationsResetRitual from "./FoundationsResetRitual";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
// Feb 24 2026 · If the browser has no token (fresh visitor / signed
// out) we skip the Authorization header entirely — sending
// `Bearer null` used to bounce the manifest fetch as 401 and hide
// every card. See the matching change in routes/foundations.py.
const auth = () => {
  const t = getToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

const TOK = {
  ink: "#0d1f3a",
  bg: "radial-gradient(ellipse at 50% 22%, #1a2e50 0%, #0d1f3a 50%, #060e1f 85%)",
  cream: "#f5ecd9",
  creamStrong: "rgba(245,236,217,0.94)",
  creamDim: "rgba(245,236,217,0.82)",
  creamMist: "rgba(245,236,217,0.55)",
  gold: "#e0a97a",
  goldWarm: "#f0c790",
  goldSoft: "rgba(224,169,122,0.9)",
  goldMist: "rgba(224,169,122,0.24)",
  goldGlow: "rgba(224,169,122,0.32)",
  divider: "rgba(245,236,217,0.09)",
};

const SERIF = '"Playfair Display", "Cormorant Garamond", serif';
const SANS = '"Manrope", "Helvetica Neue", Helvetica, Arial, sans-serif';

// ── Section groupings for the EXPLORE THE FOUNDATIONS list ─────────
// Only ready items in each group are shown to recipients. If a
// group has no ready items, we hide it entirely.
const SECTION_ORDER = [
  { key: "morning",       label: "Morning",                   slots: ["morning_light"] },
  { key: "peace",         label: "Peace & Stillness",         slots: ["peace"] },
  { key: "self_belief",   label: "Self-Belief & Trust",       slots: ["self_belief"] },
  { key: "rest",          label: "Rest & Night",              slots: ["rest", "night_master"] },
  { key: "care",          label: "Care & Shared Humanity",    slots: ["you_are_not_alone", "kindness_for_body", "difficult_day", "human_family"] },
  { key: "resets",        label: "Resets",                    slots: ["reset"] },
];

function fmtDuration(it) {
  if (!it) return "";
  const secs = Number(it.duration_seconds || 0);
  const mins = Number(it.duration_minutes || 0);
  if (secs > 0 && secs < 60) return `${secs} seconds`;
  if (mins >= 60) {
    const h = Math.round(mins / 60);
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  if (mins >= 1) return `${mins} minute${mins === 1 ? "" : "s"}`;
  return "";
}

export default function FoundationsHome() {
  const nav = useNavigate();
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGiftSource, setShowGiftSource] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [showQuickReset, setShowQuickReset] = useState(false);

  const reducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/foundations/manifest`, auth());
        if (!cancelled) setManifest(r.data);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          try { localStorage.removeItem("reclaim_token"); } catch (_) {}
          setError({ kind: "session" });
        } else {
          setError({ kind: "network" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pre-load every ready track so first-play is instant.
  useEffect(() => {
    if (!manifest?.items) return;
    const preloaders = [];
    for (const it of manifest.items) {
      if (it.audio_status === "ready" && it.audio_url) {
        try {
          const a = new Audio();
          a.preload = "auto";
          a.src = `${API}${it.audio_url}`;
          a.load();
          preloaders.push(a);
        } catch (_) { /* noop */ }
      }
    }
    return () => { preloaders.forEach((a) => { try { a.src = ""; } catch (_) {} }); };
  }, [manifest]);

  const items = manifest?.items || [];
  // A `ready` item plays either a server-side audio file OR a
  // client-side ritual (e.g. Quick Reset uses shipped audio + a
  // themed modal — no `audio_url` needed).
  const isReady = (it) =>
    it && it.audio_status === "ready" && (!!it.audio_url || !!it.client_ritual);

  const findReady = (slot, minutes) => {
    return items.find((x) => x.slot === slot && (minutes == null || x.duration_minutes === minutes) && isReady(x));
  };
  const moment = findReady("moment_for_me");
  const peace3 = findReady("peace", 3);
  const rest3 = findReady("rest", 3);

  // Which sections have anything ready? Also compute their items.
  const sections = useMemo(() => {
    return SECTION_ORDER.map((s) => {
      const sectionItems = items
        .filter((it) => s.slots.includes(it.slot))
        .filter(isReady)
        // Rest & Night — keep rest (3/7) then night_master; sort by
        // shorter first inside each slot for legibility.
        .sort((a, b) => {
          if (a.slot !== b.slot) {
            return s.slots.indexOf(a.slot) - s.slots.indexOf(b.slot);
          }
          return (a.duration_minutes || 0) - (b.duration_minutes || 0);
        });
      return { ...s, items: sectionItems };
    }).filter((s) => s.items.length > 0);
  }, [items]);

  const hasAccess = !!manifest?.has_access;
  const primary = moment;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: TOK.bg, color: TOK.creamDim }}>
        <p className="italic" style={{ fontFamily: SERIF }}>Preparing your Foundations…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: TOK.bg, color: TOK.cream }} data-testid="foundations-home">
      <ReclaimBar product="foundations" />
      {/* Orb glow — a single warm light, centre-top. Slow, restrained. */}
      <FoundationsOrb reducedMotion={reducedMotion} />

      {/* Session-expired banner — appears only on real 401/403. */}
      {error?.kind === "session" && (
        <div className="max-w-md mx-auto mt-4 px-5" data-testid="foundations-session-banner">
          <div
            className="flex items-center justify-between gap-3 rounded-full px-4 py-2"
            style={{ background: "rgba(224,169,122,0.08)", border: `1px solid ${TOK.goldMist}` }}
          >
            <span className="italic" style={{ color: TOK.creamDim, fontFamily: SERIF, fontSize: "0.95rem" }}>
              Session paused. Sign in to play.
            </span>
            <button
              onClick={() => nav("/login")}
              data-testid="foundations-session-signin"
              className="uppercase"
              style={{
                background: "transparent", color: TOK.gold, fontFamily: SANS, fontSize: "0.66rem",
                letterSpacing: "0.24em", borderBottom: `1px dashed ${TOK.goldMist}`, padding: "6px 2px",
              }}
            >
              Sign in →
            </button>
          </div>
        </div>
      )}

      <div className="max-w-md mx-auto px-5 pt-8 pb-24 relative">
        {/* Header — wordmark + FOUNDATIONS eyebrow. */}
        <div className="text-center mt-4">
          <SoulumoWord size="md" />
          <div
            className="mt-2"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.64rem",
              letterSpacing: "0.42em", textTransform: "uppercase",
            }}
          >
            Foundations
          </div>
        </div>

        {/* Welcome — verbatim per brief. */}
        <h1
          className="mt-10 text-center italic leading-tight"
          style={{
            color: TOK.cream, fontFamily: SERIF,
            fontSize: "clamp(1.6rem, 6vw, 2rem)", fontWeight: 300,
          }}
          data-testid="foundations-welcome-headline"
        >
          A little care for you.
        </h1>
        <p
          className="mt-5 text-center max-w-sm mx-auto"
          style={{
            color: TOK.creamStrong, fontFamily: SANS,
            fontSize: "clamp(0.98rem, 3.2vw, 1.05rem)", lineHeight: 1.6,
          }}
          data-testid="foundations-recognition"
        >
          You give so much of yourself.
          <br />
          This space is here to care for you.
        </p>

        {new Date().getHours() < 12 && <div className="mt-8"><CheckinCard session="morning" /></div>}
        {new Date().getHours() >= 18 && <div className="mt-8"><CheckinCard session="evening" /></div>}
        <GuideBubble to="/foundations/guide" label="Foundations" />

        {/* Opening question + TWO DOORS — Mar 2026, Care Package. */}
        <div
          className="mt-10 text-center uppercase"
          style={{
            color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
            letterSpacing: "0.42em",
          }}
          data-testid="foundations-doors-eyebrow"
        >
          What do you need right now?
        </div>
        <div className="mt-5 grid grid-cols-1 gap-3" data-testid="foundations-doors">
          <button
            type="button"
            onClick={() => nav("/foundations/care-for-me")}
            className="w-full rounded-3xl text-left"
            style={{
              background: "linear-gradient(180deg, rgba(240,199,144,0.14) 0%, rgba(224,169,122,0.08) 100%)",
              border: `1px solid ${TOK.goldMist}`,
              padding: "22px 22px",
              minHeight: 108,
              boxShadow: `0 10px 36px ${TOK.goldGlow}`,
            }}
            data-testid="foundations-door-care-for-me"
          >
            <div
              className="uppercase"
              style={{
                color: TOK.goldWarm, fontFamily: SANS,
                fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.3em",
              }}
            >
              Care for me
            </div>
            <div
              className="mt-2 italic"
              style={{
                color: TOK.cream, fontFamily: SERIF,
                fontSize: "1.25rem", fontWeight: 300, lineHeight: 1.3,
              }}
            >
              Something for what I need right now.
            </div>
            <div
              className="mt-2"
              style={{
                color: TOK.creamDim, fontFamily: SANS,
                fontSize: "0.84rem", lineHeight: 1.5,
              }}
            >
              One minute · settling · rest · peace · overwhelm · feeling supported.
            </div>
          </button>

          <button
            type="button"
            onClick={() => nav("/foundations/care-for-someone-else")}
            className="w-full rounded-3xl text-left"
            style={{
              background: "linear-gradient(180deg, rgba(245,236,217,0.06) 0%, rgba(245,236,217,0.025) 100%)",
              border: `1px solid ${TOK.goldMist}`,
              padding: "22px 22px",
              minHeight: 108,
            }}
            data-testid="foundations-door-care-for-someone-else"
          >
            <div
              className="uppercase"
              style={{
                color: TOK.goldWarm, fontFamily: SANS,
                fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.3em",
              }}
            >
              Care for someone else
            </div>
            <div
              className="mt-2 italic"
              style={{
                color: TOK.cream, fontFamily: SERIF,
                fontSize: "1.25rem", fontWeight: 300, lineHeight: 1.3,
              }}
            >
              Something I can give when someone I love needs support.
            </div>
            <div
              className="mt-2"
              style={{
                color: TOK.creamDim, fontFamily: SANS,
                fontSize: "0.84rem", lineHeight: 1.5,
              }}
            >
              Listen · understand · love. Before I help, and after I've been there.
            </div>
          </button>
        </div>
        <div className="mt-4"><GuideLiteEntry to="/foundations/guide" label="Foundations" /></div>
        {!hasAccess && (
          <p
            className="mt-6 text-center italic text-[0.95rem] leading-relaxed max-w-sm mx-auto"
            style={{ color: TOK.creamStrong, fontFamily: SERIF }}
            data-testid="foundations-no-access"
          >
            Sign in with the invitation link that came with this gift
            to open your Foundations.
          </p>
        )}

        {/* EXPLORE THE FOUNDATIONS — only sections that have ready
            items are rendered. Single-column, expandable rows. */}
        {sections.length > 0 && (
          <div className="mt-14" data-testid="foundations-explore">
            <div
              className="uppercase text-center"
              style={{
                color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem", letterSpacing: "0.4em",
              }}
            >
              Explore the Foundations
            </div>
            <div className="mt-5 space-y-2">
              {sections.map((section) => (
                <SectionAccordion
                  key={section.key}
                  section={section}
                  open={!!openSections[section.key]}
                  onToggle={() => setOpenSections((s) => ({ ...s, [section.key]: !s[section.key] }))}
                  onPlay={(id) => {
                    const it = items.find((x) => x.id === id);
                    if (it?.client_ritual === "quick_reset") {
                      setShowQuickReset(true);
                    } else {
                      nav(`/foundations/play/${id}`);
                    }
                  }}
                  hasAccess={hasAccess}
                />
              ))}
            </div>
          </div>
        )}

        {/* Footer — how this gift came to you. */}
        <div className="mt-16 flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={() => setShowGiftSource(true)}
            data-testid="foundations-gift-source-open"
            className="uppercase"
            style={{
              background: "transparent", color: TOK.creamMist, fontFamily: SANS,
              fontSize: "0.6rem", letterSpacing: "0.28em",
              padding: "10px 4px", borderBottom: `1px dashed ${TOK.goldMist}`,
              minHeight: 44,
            }}
          >
            How this gift came to you →
          </button>
          <button
            onClick={() => nav("/")}
            className="uppercase"
            style={{
              background: "transparent", color: TOK.creamMist, fontFamily: SANS,
              fontSize: "0.58rem", letterSpacing: "0.28em", padding: "10px 6px",
              minHeight: 44,
            }}
            data-testid="foundations-back"
          >
            ← Home
          </button>
        </div>

        {error?.kind === "network" && (
          <div
            className="mt-8 mx-auto rounded-3xl p-6 text-center max-w-md"
            style={{ background: "rgba(224,122,106,0.08)", border: "1px solid rgba(224,122,106,0.35)" }}
            data-testid="foundations-error"
          >
            <p
              style={{ color: TOK.creamStrong, fontFamily: SANS, fontSize: "1rem", lineHeight: 1.6 }}
            >
              Could not load Foundations. This is usually a brief hiccup — please retry in a moment.
            </p>
            <button
              onClick={() => window.location.reload()}
              data-testid="foundations-error-retry"
              className="mt-5 rounded-full uppercase"
              style={{
                background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                border: "1px solid #f5e0c5", color: "#1a0f05",
                fontFamily: SANS, fontSize: "0.72rem", fontWeight: 700,
                letterSpacing: "0.22em", padding: "13px 24px",
              }}
            >
              Try again
            </button>
          </div>
        )}
      </div>

      {showGiftSource && (
        <GiftSourceSheet
          note={manifest?.gift_note}
          accessSource={manifest?.access_source || "unknown"}
          partnerName={manifest?.partner_name}
          onClose={() => setShowGiftSource(false)}
        />
      )}

      <FoundationsResetRitual
        isOpen={showQuickReset}
        onClose={() => setShowQuickReset(false)}
      />
    </div>
  );
}

/* ─── Living Soulumo orb ─────────────────────────────────────────
 * A single warm champagne glow, breathing slowly. Softer edges (no
 * hard dark ring), so the eye reads "living light" rather than a
 * blurred graphic. Motion halts under prefers-reduced-motion. */
function FoundationsOrb({ reducedMotion }) {
  return (
    <>
      <div
        className="pointer-events-none absolute"
        style={{
          top: "-20%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 720,
          height: 720,
          background:
            "radial-gradient(circle, rgba(240,199,144,0.38) 0%, rgba(224,169,122,0.18) 22%, rgba(224,169,122,0.06) 45%, rgba(224,169,122,0) 68%)",
          filter: "blur(24px)",
          animation: reducedMotion ? undefined : "found-orb-breathe 12s ease-in-out infinite",
        }}
        aria-hidden
        data-testid="foundations-orb"
      />
      <style>{`
        @keyframes found-orb-breathe {
          0%, 100% { opacity: 0.88; transform: translateX(-50%) scale(1); }
          50%      { opacity: 1;    transform: translateX(-50%) scale(1.04); }
        }
      `}</style>
    </>
  );
}

/* ─── Chooser — three quiet pills. Only shown when target ready. ── */
function Chooser({ items, onPick }) {
  const availableItems = items.filter((it) => !!it.target);
  if (availableItems.length === 0) return null;
  return (
    <div className="mt-12" data-testid="foundations-chooser">
      <div
        className="uppercase text-center"
        style={{
          color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem", letterSpacing: "0.34em",
        }}
      >
        What do you need right now?
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2.5">
        {availableItems.map((it) => (
          <button
            key={it.key}
            type="button"
            onClick={() => onPick(it.target)}
            data-testid={`foundations-chooser-${it.key}`}
            className="flex items-center justify-between rounded-full"
            style={{
              background: "rgba(224,169,122,0.10)",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.cream,
              padding: "16px 22px",
              minHeight: 56,
              cursor: "pointer",
            }}
          >
            <span
              className="italic text-left"
              style={{ fontFamily: SERIF, fontSize: "1.1rem", color: TOK.cream }}
            >
              {it.label}
            </span>
            <span
              className="uppercase whitespace-nowrap"
              style={{ color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.6rem", letterSpacing: "0.22em" }}
            >
              {fmtDuration(it.target)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Section accordion row ────────────────────────────────────── */
function SectionAccordion({ section, open, onToggle, onPlay, hasAccess }) {
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: "rgba(245,236,217,0.035)",
        border: `1px solid ${TOK.goldMist}`,
      }}
      data-testid={`foundations-section-${section.key}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`foundations-section-toggle-${section.key}`}
        className="w-full flex items-center justify-between"
        style={{
          background: "transparent",
          padding: "16px 20px",
          minHeight: 60,
          color: TOK.cream,
          cursor: "pointer",
        }}
      >
        <span
          className="italic text-left"
          style={{ fontFamily: SERIF, fontSize: "1.15rem", color: TOK.cream }}
        >
          {section.label}
        </span>
        <span className="flex items-center gap-3">
          <span
            className="uppercase"
            style={{ color: TOK.creamMist, fontFamily: SANS, fontSize: "0.58rem", letterSpacing: "0.24em" }}
          >
            {section.items.length}
          </span>
          <span
            style={{
              display: "inline-block",
              transition: "transform 220ms ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              color: TOK.gold,
              fontSize: "0.9rem",
              lineHeight: 1,
            }}
            aria-hidden
          >
            ⌄
          </span>
        </span>
      </button>
      {open && (
        <div
          className="border-t px-4 pb-4 pt-2 space-y-2"
          style={{ borderColor: TOK.divider }}
        >
          {section.items.map((it) => (
            <CollectionRow
              key={it.id}
              item={it}
              hasAccess={hasAccess}
              onPlay={onPlay}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── One row inside an accordion. Title · description · duration
 * · single play control. No dev language, no duplicate duration. */
function CollectionRow({ item, hasAccess, onPlay }) {
  const playable =
    hasAccess &&
    item.audio_status === "ready" &&
    (!!item.audio_url || !!item.client_ritual);
  const duration = fmtDuration(item);
  const isNight = item.slot === "night_master";
  return (
    <button
      type="button"
      onClick={() => playable && onPlay(item.id)}
      disabled={!playable}
      data-testid={`foundations-card-${item.id}`}
      className="w-full rounded-2xl text-left flex items-center justify-between gap-3"
      style={{
        background: isNight
          ? "linear-gradient(135deg, rgba(140,110,210,0.18) 0%, rgba(224,169,122,0.06) 100%)"
          : "linear-gradient(135deg, rgba(224,169,122,0.10) 0%, rgba(245,236,217,0.03) 100%)",
        border: `1px solid ${isNight ? "rgba(140,110,210,0.32)" : TOK.goldMist}`,
        color: TOK.cream,
        padding: "14px 16px",
        minHeight: 76,
        cursor: playable ? "pointer" : "default",
      }}
    >
      <div className="flex-1 min-w-0">
        <div
          className="italic"
          style={{
            fontFamily: SERIF, color: TOK.cream, fontSize: "1.08rem",
            fontWeight: 300, lineHeight: 1.2,
          }}
        >
          {item.label.split(" · ")[0]}
        </div>
        <div
          className="mt-1"
          style={{
            color: TOK.creamStrong, fontFamily: SANS,
            fontSize: "0.85rem", lineHeight: 1.45,
          }}
        >
          {item.blurb}
        </div>
        <div
          className="mt-2 uppercase"
          style={{
            color: TOK.goldSoft, fontFamily: SANS,
            fontSize: "0.56rem", letterSpacing: "0.28em",
          }}
        >
          {duration}
        </div>
      </div>
      <PlayCircle isNight={isNight} />
    </button>
  );
}

function PlayCircle({ isNight }) {
  return (
    <div
      aria-hidden
      style={{
        width: 44, height: 44, minWidth: 44,
        borderRadius: 999,
        background: isNight
          ? "linear-gradient(180deg, #b898e8 0%, #7f5cc7 100%)"
          : "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
        border: "1px solid rgba(255,255,255,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: isNight ? "#1a0f30" : "#1a0f05",
        boxShadow: isNight
          ? "0 0 24px rgba(140,110,210,0.35)"
          : "0 0 18px rgba(224,169,122,0.35)",
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 2 }}>
        <path d="M7 5v14l12-7z" />
      </svg>
    </div>
  );
}

/* ─── Warm, source-aware "How this gift came to you" ────────── */
function GiftSourceSheet({ note, accessSource, partnerName, onClose }) {
  // Prefer the backend-supplied warm note when present. Fall back to
  // source-aware defaults when it isn't.
  const opening = note && String(note).trim().length > 0
    ? note
    : (accessSource === "community_partner" && partnerName
        ? `Your access came to you through ${partnerName}, a caring partner working with people who give so much of themselves.`
        : "Soulumo Foundations was created to return a little care to the people who give so much. Your access has been gifted through One Million Lights and its caring partners, helping these moments of support reach carers and the people they care for.");
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: "rgba(6,14,31,0.94)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}
      data-testid="foundations-gift-source-sheet"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-md mx-auto px-6 py-12"
      >
        <button
          onClick={onClose}
          data-testid="foundations-gift-source-close"
          className="uppercase"
          style={{
            background: "transparent", color: TOK.goldSoft, fontFamily: SANS,
            fontSize: "0.66rem", letterSpacing: "0.28em", minHeight: 44, padding: "8px 4px",
          }}
        >
          ← Close
        </button>
        <div
          className="mt-8 uppercase text-center"
          style={{
            color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem", letterSpacing: "0.4em",
          }}
        >
          How this gift came to you
        </div>
        <p
          className="mt-6 italic leading-relaxed text-center"
          style={{ color: TOK.cream, fontFamily: SERIF, fontSize: "1.1rem" }}
        >
          {opening}
        </p>
        <div className="mt-10 flex justify-center">
          <a
            href="/?brand=oml"
            data-testid="foundations-gift-source-oml"
            className="uppercase"
            style={{
              color: TOK.gold, fontFamily: SANS, fontSize: "0.66rem",
              letterSpacing: "0.28em", borderBottom: `1px dashed ${TOK.goldMist}`,
              padding: "8px 2px", minHeight: 44, display: "inline-flex", alignItems: "center",
            }}
          >
            Discover One Million Lights →
          </a>
        </div>
      </div>
    </div>
  );
}



